import { useTranslation } from '@amusnet/translations/react'
import { useConfigurationStore, useJackpotStore, useMenuStore, useNavigationStore } from '@stores'
import {
    GameUIMessageScope,
    GameUIMessageType,
    ToggleBatterySaverMessage,
    ToggleLeftHandModeMessage,
    ViewportType,
} from 'game-ui-types'

export const useGameSettingsItems = () => {
    const { t } = useTranslation()

    const isQuickSpinOn = useMenuStore((state) => state.isQuickSpinOn)
    const setIsQuickSpinOn = useMenuStore((state) => state.setIsQuickSpinOn)
    const isBatterySaverOn = useMenuStore((state) => state.isBatterySaverOn)
    const setIsBatterySaverOn = useMenuStore((state) => state.setIsBatterySaverOn)
    const isLeftHandModeOn = useMenuStore((state) => state.isLeftHandModeOn)
    const setIsLeftHandModeOn = useMenuStore((state) => state.setIsLeftHandModeOn)
    const isEQJackpotType = useJackpotStore((state) => state.isEQJackpotType)

    const hasBackgroundMusic = useNavigationStore((state) => state.hasBackgroundMusic)

    const setIsSoundMuted = useConfigurationStore((state) => state.setIsSoundMuted)
    const shouldShowLeftHandModeButton = useConfigurationStore((state) => state.shouldShowLeftHandModeButton)
    const isSoundMuted = useConfigurationStore((state) => state.isSoundMuted)
    const setIsBackgroundMusicMuted = useConfigurationStore((state) => state.setIsBackgroundMusicMuted)
    const isBackgroundMusicMuted = useConfigurationStore((state) => state.isBackgroundMusicMuted)
    const viewport = useConfigurationStore((state) => state.viewport)
    const hasBuyBonus = useConfigurationStore((state) => state.hasBuyBonus)
    const minimumSpinTime = useConfigurationStore((state) => state.minimumSpinTimeDuration)

    const handleSoundToggled = () => {
        setIsSoundMuted(!isSoundMuted)
    }

    const handleBackgroundMusicToggled = () => {
        setIsBackgroundMusicMuted(!isBackgroundMusicMuted)
    }

    const handleQuickSpinToggled = () => {
        setIsQuickSpinOn(!isQuickSpinOn)

        const toggleQuickSpinMessage: ToggleLeftHandModeMessage | unknown = {
            type: GameUIMessageType.TOGGLE_QUICK_SPIN_MODE,
            payload: isQuickSpinOn,
            scope: GameUIMessageScope.MENU,
        }

        window.postMessage(toggleQuickSpinMessage)
    }

    const handleBatterySaverToggled = () => {
        const updatedIsBatterySaverOn = !isBatterySaverOn

        setIsBatterySaverOn()

        const toggleBatterySaverMessage: ToggleBatterySaverMessage = {
            type: GameUIMessageType.TOGGLE_BATTERY_SAVER_MODE,
            payload: updatedIsBatterySaverOn,
            scope: GameUIMessageScope.MENU,
        }

        window.postMessage(toggleBatterySaverMessage)
    }

    const items = (
        [
            { label: 'gameui.sound', onClick: handleSoundToggled, isToggled: !isSoundMuted },
            {
                label: 'gameui.backgroundSound',
                onClick: handleBackgroundMusicToggled,
                isToggled: !isSoundMuted && !isBackgroundMusicMuted,
                disabled: isSoundMuted,
            },
            { label: 'gameui.leftHandMode', onClick: setIsLeftHandModeOn, isToggled: isLeftHandModeOn },
            { label: 'gameui.quickSpinMode', onClick: handleQuickSpinToggled, isToggled: isQuickSpinOn },
            { label: 'gameui.batterySaverMode', onClick: handleBatterySaverToggled, isToggled: isBatterySaverOn },
        ] as const
    )
        .filter((item) => {
            if (
                item.label === 'gameui.leftHandMode' &&
                (viewport === ViewportType.DESKTOP || hasBuyBonus || isEQJackpotType || !shouldShowLeftHandModeButton)
            )
                return false

            if (item.label === 'gameui.backgroundSound' && !hasBackgroundMusic) return false

            if (item.label === 'gameui.quickSpinMode' && minimumSpinTime > 0) return false

            if (item.label === 'gameui.batterySaverMode') return false

            return true
        })
        .map((item) => ({ ...item, label: t(item.label) }))

    return items
}
